/**
 * Logica pura delle 6 caselle del codice 2FA (digitazione, incolla, compilazione automatica del telefono).
 * Restituisce il nuovo stato, dove mettere il cursore e, se il codice e' completo, il codice da verificare.
 */
export const CODE_LENGTH = 6;

export interface CodeInputResult {
  code: string[];
  /** Casella su cui spostare il cursore (null = resta dove sei) */
  focus: number | null;
  /** Codice completo da verificare subito, altrimenti null */
  complete: string | null;
}

export function emptyCode(): string[] {
  return Array.from({ length: CODE_LENGTH }, () => '');
}

function digitsOf(text: string): string {
  return String(text ?? '').replace(/\D/g, '');
}

function finish(code: string[], focus: number | null): CodeInputResult {
  const joined = code.join('');
  return { code, focus, complete: joined.length === CODE_LENGTH && code.every((d) => d !== '') ? joined : null };
}

/**
 * Testo incollato (o inserito dal telefono) nella casella `index`.
 * Con 6 o piu' cifre riempie tutto da capo, qualunque sia la casella; con meno riempie da `index` in avanti.
 */
export function applyPaste(code: string[], index: number, text: string): CodeInputResult {
  const digits = digitsOf(text);
  if (!digits) return { code, focus: null, complete: null };
  if (digits.length >= CODE_LENGTH) {
    const full = digits.slice(0, CODE_LENGTH).split('');
    return finish(full, CODE_LENGTH - 1);
  }
  const next = [...code];
  let pos = Math.max(0, Math.min(index, CODE_LENGTH - 1));
  for (const d of digits) {
    if (pos >= CODE_LENGTH) break;
    next[pos] = d;
    pos += 1;
  }
  return finish(next, Math.min(pos, CODE_LENGTH - 1));
}

/** Valore arrivato dall'evento change della casella `index`. */
export function applyDigitInput(code: string[], index: number, rawValue: string): CodeInputResult {
  const digits = digitsOf(rawValue);
  if (rawValue !== '' && !digits) return { code, focus: null, complete: null };
  // Piu' cifre insieme: compilazione automatica (autocomplete one-time-code) o incolla senza evento paste
  if (digits.length > 1) {
    // Se la casella aveva gia' una cifra, il browser aggiunge la nuova in coda: teniamo l'ultima
    const typedOver = code[index] && digits.length === 2 && digits.startsWith(code[index]);
    if (!typedOver) return applyPaste(code, index, digits);
  }
  const next = [...code];
  next[index] = digits.slice(-1);
  const focus = next[index] && index < CODE_LENGTH - 1 ? index + 1 : null;
  return finish(next, focus);
}

/** Backspace su casella vuota: torna indietro e cancella la cifra precedente. */
export function applyBackspace(code: string[], index: number): CodeInputResult {
  if (code[index]) {
    const next = [...code];
    next[index] = '';
    return { code: next, focus: index, complete: null };
  }
  if (index === 0) return { code, focus: 0, complete: null };
  const next = [...code];
  next[index - 1] = '';
  return { code: next, focus: index - 1, complete: null };
}
